import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import {
  createUser,
  deleteUser,
  getUsers,
  updateUser,
  updateUserPassword,
} from "../api/userApi";


const { Title } = Typography;

function getRoleColor(role) {
  if (role === "ADMIN") return "red";
  if (role === "MODERATOR") return "blue";
  if (role === "AUTHOR") return "gold";
  return "default";
}

function normalizeUsers(data) {
  return (data || []).map((item) => ({
    ...item,
    key: item.id,
  }));
}

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();


  const adminUser = useMemo(
    () => users.find((user) => user.role === "ADMIN") || null,
    [users],
  );

  const hasAdmin = !!adminUser;

  const createRoleOptions = useMemo(() => {
    const base = [
      { label: "MODERATOR", value: "MODERATOR" },
      { label: "AUTHOR", value: "AUTHOR" },
      { label: "CUSTOMER", value: "CUSTOMER" },
    ];

    if (!hasAdmin) {
      return [{ label: "ADMIN", value: "ADMIN" }, ...base];
    }

    return base;
  }, [hasAdmin]);

  const getEditRoleOptions = (user) => {
    const base = [
      { label: "MODERATOR", value: "MODERATOR" },
      { label: "AUTHOR", value: "AUTHOR" },
      { label: "CUSTOMER", value: "CUSTOMER" },
    ];

    if (!hasAdmin) {
      return [{ label: "ADMIN", value: "ADMIN" }, ...base];
    }

    if (user?.role === "ADMIN") {
      return [{ label: "ADMIN", value: "ADMIN" }, ...base];
    }

    return base;
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const data = await getUsers();
      setUsers(normalizeUsers(data));
    } catch (error) {
      message.error(error.message || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const openCreateModal = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      username: "",
      password: "",
      role: hasAdmin ? "AUTHOR" : "ADMIN",
    });
    setCreateOpen(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      username: user.username,
      role: user.role,
    });
    setEditOpen(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    passwordForm.resetFields();
    setPasswordOpen(true);
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    createForm.resetFields();
  };

  const closeEditModal = () => {
    setEditOpen(false);
    setSelectedUser(null);
    editForm.resetFields();
  };

  const closePasswordModal = () => {
    setPasswordOpen(false);
    setSelectedUser(null);
    passwordForm.resetFields();
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();

      if (values.role === "ADMIN" && hasAdmin) {
        message.error("Only one ADMIN is allowed");
        return;
      }

      const payload = {
        username: values.username,
        password: values.password,
        role: values.role,
      };

      await createUser(payload);
      message.success("User created");
      closeCreateModal();
      loadUsers();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || "Failed to create user");
    }
  };

  const handleEdit = async () => {
    try {
      const values = await editForm.validateFields();

      const changingAnotherUserToAdmin =
        values.role === "ADMIN" &&
        hasAdmin &&
        adminUser?.id !== selectedUser?.id;

      if (changingAnotherUserToAdmin) {
        message.error("Only one ADMIN is allowed");
        return;
      }

      const payload = {
        username: values.username,
        role: values.role,
      };

      await updateUser(selectedUser.id, payload);
      message.success("User updated");
      closeEditModal();
      loadUsers();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || "Failed to update user");
    }
  };

  const handlePasswordUpdate = async () => {
    try {
      const values = await passwordForm.validateFields();

      await updateUserPassword(selectedUser.id, {
        password: values.password,
      });

      message.success("Password updated");
      closePasswordModal();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || "Failed to update password");
    }
  };

  const handleDelete = async (user) => {
    try {
      if (user.role === "ADMIN") {
        message.error("ADMIN user cannot be deleted");
        return;
      }

      await deleteUser(user.id);
      message.success("User deleted");
      loadUsers();
    } catch (error) {
      message.error(error.message || "Failed to delete user");
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      // No width — let this column fill remaining space
      ellipsis: true,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 160,
      render: (value) => (
        <Tag color={getRoleColor(value)}>
          {value}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            onClick={() => openEditModal(record)}
          >
            Edit
          </Button>

          <Button
            type="link"
            size="small"
            onClick={() => openPasswordModal(record)}
          >
            Password
          </Button>

          <Popconfirm
            title="Delete user?"
            description={
              record.role === "ADMIN"
                ? "ADMIN user cannot be deleted"
                : `Delete ${record.username}?`
            }
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
            disabled={record.role === "ADMIN"}
          >
            <Button
              type="link"
              danger
              size="small"
              disabled={record.role === "ADMIN"}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6 max-w-6xl mx-auto px-4">
      <Card>
        <div className="flex items-center justify-between">
          <Title level={3} className="!mb-0">
            Users
          </Title>

          <Space>
            {adminUser && <Tag color="red">ADMIN: {adminUser.username}</Tag>}

            <Button type="primary" onClick={openCreateModal}>
              Add User
            </Button>
          </Space>
        </div>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={usersLoading}
          bordered
          tableLayout="fixed"
          pagination={{
            position: ["bottomRight"],
            showSizeChanger: false,
          }}
        />
      </Card>

      <Modal
        open={createOpen}
        title="Add User"
        onCancel={closeCreateModal}
        onOk={handleCreate}
        okText="Create"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: "Username is required" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: "Password is required" }]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select options={createRoleOptions} />
          </Form.Item>

        </Form>
      </Modal>

      <Modal
        open={editOpen}
        title="Edit User"
        onCancel={closeEditModal}
        onOk={handleEdit}
        okText="Save"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: "Username is required" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select options={getEditRoleOptions(selectedUser)} />
          </Form.Item>

        </Form>
      </Modal>

      <Modal
        open={passwordOpen}
        title={`Change Password${selectedUser ? ` - ${selectedUser.username}` : ""}`}
        onCancel={closePasswordModal}
        onOk={handlePasswordUpdate}
        okText="Update"
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="password"
            label="New Password"
            rules={[{ required: true, message: "Password is required" }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default UsersPage;
